import React, { useEffect, useState } from "react";
import { useFeathers } from "../../app/util";
import { SearchableSelectInput } from "../SearchableSelectInput";
import { name } from '../../app/util/clean-name';
import CandidateNoImage from '../CandidateNamePhoto/candidate-icon.png';
import moment from 'moment';
import styled from 'styled-components';
import { Typography } from "@mui/material";

const formatProfileStatus = (recentCandidate) => {
  if (recentCandidate?.workflow?.status !== 'published') return 'No profile';

  const updatedAt = moment.utc(recentCandidate?.updatedAt);
  return updatedAt.isValid()
    ? `Profiled ${updatedAt.format('LL')}`
    : 'Profiled';
};

const OfficialOption = ({
  name = 'Official',
  photo,
  profileStatus
}) => (
  <OfficialOptionWrapper>
    <img src={photo || CandidateNoImage} alt="" />
    <div>
      <Typography variant="body1">{name}</Typography>
      <Typography variant="body2" style={{ fontSize: '12px', marginTop: '-4px', opacity: 0.7 }}>{profileStatus}</Typography>
    </div>
  </OfficialOptionWrapper>
);

const OfficialsSelectInput = ({
  onChange,
  value,
  style,
  className,
  office,
  district,
  seedSearchText = '',
  placeholder = 'Search office holders...',
  allowedOfficialKeys = null,
  disabled = false,
  allowNewOption = true,
  clearAfterSelect = false
}) => {
	const feathers = useFeathers();

	const [	options, setOptions] = useState(null);
	const [ loading, setLoading ] = useState(false);

  const searchForOfficialField = async (searchText) => {
		setLoading(true)
    const officials = await feathers
      .getService("officials")
      .find({
        query: searchText ? { $search: searchText } : {},
      });
    const keyFilter = Array.isArray(allowedOfficialKeys) ? new Set(allowedOfficialKeys) : null;
    const options = officials.data
      .filter(off => !keyFilter || keyFilter.has(off.key))
      .map((off) => ({
        ...off,
        key: off.key,
        photo:
          off.recentCandidate?.stagingDraft?.photoPathFace ||
          off.recentCandidate?.photoPathFace,
        name:
          off.recentCandidate?.stagingDraft?.name || off.recentCandidate?.name || off.name,
        profileStatus: formatProfileStatus(off.recentCandidate),
      }))
      .map((o) => ({ ...o, text: o.name, photo: o.photo }));
		setOptions(options);
		setLoading(false)
  };

  useEffect(() => {
    if(!Array.isArray(allowedOfficialKeys) || disabled) return;
    if(allowedOfficialKeys.length === 0) {
      setOptions([]);
      return;
    }
    searchForOfficialField('');
  }, [allowedOfficialKeys, disabled]);

  return (
		<SearchableSelectInput
			name='official'
			onChangeSearchText={searchForOfficialField}
			modifySearchText={(textPrev, textNew) => {
				// if we just pasted a bunch of text, clean each candidate name
				if ((textNew || '')?.length - 5 > (textPrev || '')?.length)
					return name(textNew)
				else
					return textNew;
			}}
			value={value}
			onChange={onChange}
			optionsLoading={loading}
			options={options || []}
			style={style}
			className={className}
			seedSearchText={seedSearchText}
			placeholder={placeholder}
			searchOptionComponent={OfficialOption}
      disabled={disabled}
			allowNewOption={allowNewOption && !Array.isArray(allowedOfficialKeys)}
			clearAfterSelect={clearAfterSelect}
			formatNewOptionFromSearchText={(text) => `Create a new candidate named "${text}"`}
			onAddNewOption={(value) => onChange({ target: { isNew: true, name: 'official', value: { name: value, text: value, isNew: true} } })}
		/>
	)
}

const OfficialOptionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 42px;

  p {
    color: #707683;
    font-size: 16px;
    margin: 0;
  }

  span {
    color: #707683;
    display: block;
    font-size: 12px;
    line-height: 1.2;
    margin-top: 2px;
    opacity: 0.7;
  }

  img {
    height: 30px;
    width: 30px;
    overflow: 0;
    margin-right: 6px;
    border-radius: 15px;
  }
`;

export { OfficialsSelectInput };